import { useMemo } from 'react';
import { BigNum, QUOTE_PRECISION_EXP, ZERO } from '@velocity-exchange/sdk';
import { formatText, PRESETS } from '@velocity-exchange/common/format';
import useVelocityClient from './useVelocityClient';
import useVelocityClientIsReady from './useVelocityClientIsReady';
import useWalletIsConnected from './useWalletIsConnected';
import useUserAccountIsReady from './useUserAccountIsReady';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import {
	isIsolatedPositionBeingLiquidated,
	getHealthWithOverride,
} from 'src/utils/dev/devLiquidationOverrides';
import { useIsolatedPositionMargin } from './positions/useIsolatedPositionMargin';

export const usePositionSummary = (selection: 'cross' | `iso-${number}`) => {
	const connected = useWalletIsConnected();
	const velocityClient = useVelocityClient();
	const isVelocityClientReady = useVelocityClientIsReady();
	const userAccountIsReady = useUserAccountIsReady();
	const currentUserKey = useVelocityAccountStore((s) => s.currentUserKey);
	const userClient =
		isVelocityClientReady && userAccountIsReady && velocityClient?.hasUser()
			? velocityClient.getUser()
			: undefined;

	// Compute margin calculation once per render for maintenance category
	const marginCalc = useMemo(() => {
		return userClient && userAccountIsReady
			? userClient.getMarginCalculation('Maintenance')
			: undefined;
	}, [userClient, userAccountIsReady]);

	const perpMarketIndex = useMemo(() => {
		if (selection.startsWith('iso-')) {
			return Number(selection.split('iso-')[1]);
		}
		return undefined;
	}, [selection]);

	const positionIsolatedMargin = useIsolatedPositionMargin(
		perpMarketIndex ?? 0
	);

	const { healthPercentFormatted, health, totalCollateral, maintenanceMargin } =
		useMemo(() => {
			if (!userClient || !marginCalc) {
				return {
					healthPercentFormatted: '100.0%',
					health: 100,
					totalCollateral: formatText(0, PRESETS.usd),
					maintenanceMargin: formatText(0, PRESETS.usd),
					perpMarketIndex: undefined as number | undefined,
				};
			}

			let computedHealth = 100;
			let computedHealthPercent = '100.0%';
			let totalCollBn = ZERO;
			let maintReqBn = ZERO;
			let marketIndex: number | undefined = undefined;

			if (selection.startsWith('iso-')) {
				const idx = Number(selection.split('iso-')[1]);
				marketIndex = idx;
				const iso = marginCalc.isolatedMarginCalculations.get(idx);
				const isIsoBeingLiquidated =
					currentUserKey &&
					isIsolatedPositionBeingLiquidated(userClient, idx, currentUserKey);
				if (isIsoBeingLiquidated) {
					computedHealth = 0;
					computedHealthPercent = 'Liquidating';
				} else {
					if (iso) {
						totalCollBn = iso.totalCollateral;
						maintReqBn = iso.marginRequirement;
					}
					computedHealth =
						connected && userAccountIsReady
							? getHealthWithOverride(userClient, idx, currentUserKey)
							: 100;
					computedHealthPercent = `${computedHealth.toFixed(1)}%`;
				}
			} else {
				totalCollBn = marginCalc.totalCollateral;
				maintReqBn = marginCalc.marginRequirement;
				computedHealth =
					connected && userAccountIsReady ? userClient.getHealth() : 100;
				computedHealthPercent = userClient.isCrossMarginBeingLiquidated()
					? 'Liquidating'
					: `${computedHealth.toFixed(1)}%`;
			}

			return {
				healthPercentFormatted: computedHealthPercent,
				health: computedHealth,
				totalCollateral: formatText(
					BigNum.from(totalCollBn, QUOTE_PRECISION_EXP).toNum(),
					PRESETS.usd
				),
				maintenanceMargin: formatText(
					BigNum.from(maintReqBn, QUOTE_PRECISION_EXP).toNum(),
					PRESETS.usd
				),
				perpMarketIndex: marketIndex,
			};
		}, [
			selection,
			userClient,
			marginCalc,
			connected,
			userAccountIsReady,
			currentUserKey,
			positionIsolatedMargin,
		]);

	return {
		healthPercentFormatted,
		health,
		totalCollateral,
		maintenanceMargin,
		perpMarketIndex,
	};
};
